/**
 * AI review stream viewer.
 *
 * On mount: fetches the review's current state. Behaviour by status:
 *   - complete  → render the saved body, done.
 *   - failed    → render the error.
 *   - pending   → open SSE stream; render tokens as they arrive.
 *   - streaming → another tab is already streaming; show a "wait" UI
 *                 + poll for completion.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  aireviewApi,
  type AIPersonalityMeta,
  type AIReviewDetail,
} from '@/lib/api';
import { openSSE } from '@/lib/sse';
import { friendlyError } from '@/lib/errors';

interface DoneEventData {
  id: string;
  tokens_in: number;
  tokens_out: number;
  completed_at?: string;
}

export function AIReviewPage() {
  const { id } = useParams<{ id: string }>();
  const [review, setReview] = useState<AIReviewDetail | null>(null);
  const [streamedBody, setStreamedBody] = useState('');
  const [personalities, setPersonalities] = useState<AIPersonalityMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const closeRef = useRef<(() => void) | null>(null);

  // Pull review + personality metas in parallel on mount. Auth gate
  // sits on AppShell so we don't re-check it here.
  useEffect(() => {
    if (!id) return;
    aireviewApi.get(id).then(setReview).catch((e) => {
      setError(friendlyError(e, 'AIReview'));
    });
    aireviewApi.personalities().then((r) => setPersonalities(r.personalities)).catch(() => null);

    return () => closeRef.current?.();
  }, [id]);

  // Once we have the review, kick off the stream if it's pending.
  useEffect(() => {
    if (!review || !id) return;
    if (review.status !== 'pending') return;

    setStreaming(true);
    const ctrl = openSSE(aireviewApi.streamPath(id), {
      onEvent: (name, data) => {
        if (name === 'content' && typeof data === 'string') {
          setStreamedBody((b) => b + data);
        } else if (name === 'done') {
          setStreaming(false);
          const d = data as DoneEventData;
          // Refresh the full row to pick up the final body + tokens.
          aireviewApi.get(id).then(setReview).catch(() => null);
          void d;
        } else if (name === 'error') {
          setStreaming(false);
          const d = data as { code?: string; message?: string };
          setError(d.message ?? 'Stream failed.');
        }
      },
      onClose: () => setStreaming(false),
      onError: (e) => {
        setStreaming(false);
        setError(e.message);
      },
    });
    ctrl.then((c) => {
      closeRef.current = c.close;
    }).catch((e) => {
      setStreaming(false);
      setError(friendlyError(e, 'AIReview'));
    });
  }, [review, id]);

  const personalityMeta = personalities.find((p) => p.id === review?.personality);
  // What to render in the body area: live stream while pending, saved body otherwise.
  const renderedBody =
    review?.status === 'pending' || streaming ? streamedBody : (review?.body ?? '');

  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px' }}>
      <Link to="/ai" style={backLink}>← All personalities</Link>

      {review && (
        <header style={{ marginTop: 12, marginBottom: 28 }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            {statusLabel(review.status)}
          </div>
          <h1 className="serif" style={{ fontSize: 36, fontStyle: 'italic', fontWeight: 500, marginBottom: 6 }}>
            {personalityMeta?.display_name ?? review.personality}
          </h1>
          {personalityMeta && (
            <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>{personalityMeta.tagline}</p>
          )}
        </header>
      )}

      {error && <div style={errorBox}>{error}</div>}

      {/* The review body. Whitespace-preserved so the LLM's paragraph
          breaks render — Claude tends to emit \n\n between paragraphs. */}
      {renderedBody && (
        <article style={bodyStyle}>
          {renderedBody}
          {streaming && <span style={cursor}>▍</span>}
        </article>
      )}

      {/* Footer: provenance metadata only. Re-roll was removed — the
          rule is one review per (personality, profile_version), so the
          only way to get a fresh one is to update the profile + come
          back to the picker. */}
      {review?.status === 'complete' && (
        <footer style={{ marginTop: 32 }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}>
            {review.tokens_out} tokens · {review.provider ?? 'claude'} · {new Date(review.completed_at ?? '').toLocaleString()}
          </div>
        </footer>
      )}

      {review?.status === 'streaming' && !streaming && (
        <div style={infoBox}>
          This review is being streamed in another tab. Close it there and reload here, or wait for it to finish.
        </div>
      )}
    </main>
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case 'pending':
    case 'streaming':
      return 'Generating…';
    case 'complete':
      return 'Review';
    case 'failed':
      return 'Failed';
  }
  return status;
}

const backLink: React.CSSProperties = {
  color: 'var(--ink-3)',
  fontSize: 13,
  textDecoration: 'none',
};

const bodyStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  fontFamily: 'var(--font-display)',
  fontSize: 18,
  lineHeight: 1.6,
  color: 'var(--ink)',
  background: 'var(--surface)',
  borderRadius: 18,
  padding: 28,
  boxShadow: 'var(--shadow-card)',
  minHeight: 200,
};

const cursor: React.CSSProperties = {
  color: 'var(--coral)',
  animation: 'blink 1s steps(2, start) infinite',
  marginLeft: 2,
};

const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
  marginBottom: 20,
};

const infoBox: React.CSSProperties = {
  background: 'rgba(43, 29, 20, 0.04)',
  color: 'var(--ink-2)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 13,
  marginTop: 24,
};

